import { httpsCallable } from 'firebase/functions';
import type { AreaId } from '@shared/brand';
import { err, ok, type Result } from '@shared/result';
import type {
  IOrgChartRepository,
  RepositoryError,
  SaveOrgChartInput,
} from '@application/ports';
import type { OrgChart } from '@domain/entities/OrgChart';
import { decodeOrgChart } from '../codecs/orgChart';
import { firebase } from '../firebase/client';
import { translateCallableError } from './support';

/**
 * El organigrama contra el servidor: dos callables y ninguna lectura directa.
 *
 * `orgCharts` no tiene regla en `firestore.rules` y por eso ningún cliente lo lee
 * ni lo escribe por su cuenta: verlo depende de `ver_organigrama`, un permiso de
 * la matriz, y las reglas no leen la matriz. `getOrgChart` y `saveOrgChart` sí.
 */

type ChartPayload = { readonly chart: unknown };

const malformed = (areaId: AreaId): RepositoryError => ({
  kind: 'malformed-document',
  path: `orgCharts/${areaId}`,
  detail: 'El organigrama guardado no se pudo leer.',
});

export class FirestoreOrgChartRepository implements IOrgChartRepository {
  async get(areaId: AreaId): Promise<Result<OrgChart | null, RepositoryError>> {
    try {
      const { functions } = firebase();
      const invoke = httpsCallable<{ areaId: string }, ChartPayload>(functions, 'getOrgChart');
      const { data } = await invoke({ areaId });
      /* Sin documento no es un error: el área todavía no tiene organigrama. */
      if (data.chart === null) return ok(null);
      const chart = decodeOrgChart(areaId, data.chart);
      return chart === null ? err(malformed(areaId)) : ok(chart);
    } catch (cause) {
      return err(translateCallableError(cause, 'getOrgChart'));
    }
  }

  async save(input: SaveOrgChartInput): Promise<Result<OrgChart, RepositoryError>> {
    try {
      const { functions } = firebase();
      const invoke = httpsCallable<
        { areaId: string; body: { nodes: unknown[]; links: unknown[] } },
        ChartPayload
      >(functions, 'saveOrgChart');
      const { data } = await invoke({
        areaId: input.areaId,
        body: { nodes: [...input.body.nodes], links: [...input.body.links] },
      });
      const chart = decodeOrgChart(input.areaId, data.chart);
      return chart === null ? err(malformed(input.areaId)) : ok(chart);
    } catch (cause) {
      return err(translateCallableError(cause, 'saveOrgChart'));
    }
  }
}
